/**
 * Datora Upsell Slider
 *
 * Shows eligible discounted products in the cart drawer
 * when qualifying trigger products are in the cart.
 *
 * Data source: co2 (Datora Multi Discounts app)
 * Entry point: initUpsellSlider()
 * Triggered by: 'cart:rerendered' event (dispatched from theme.js after cart drawer DOM swap)
 */

var isInitializing = false;
var pendingInit = false;
var productDataCache = new Map();
var storefrontClient = null;
var flickityInstance = null;

// ─── 1. Entry point ────────────────────────────────────────

initUpsellSlider = async function () {
  var container = getSliderContainer();
  if (!container) return;

  if (isInitializing) { pendingInit = true; return; }
  isInitializing = true;
  pendingInit = false;

  try {
    var settings = getSettings(container);

    var result = await collectEligibleProducts();
    renderSlider(result.products, result.totalRemaining, settings);
  } catch (e) {
    var c = getSliderContainer();
    if (c) c.hidden = true;
  } finally {
    isInitializing = false;
    if (pendingInit) {
      pendingInit = false;
      initUpsellSlider();
    }
  }
};

// ─── 2. Data collection ────────────────────────────────────

async function collectEligibleProducts() {
  var cart = await fetchCart();
  if (!cart || !cart.items) return { products: [], totalRemaining: 0 };

  var matchingRules = await getMatchingRules(cart);
  var eligibleMap = await collectEligibleHandles(matchingRules);
  var totalRemaining = sumRemaining(matchingRules);
  var products = await fetchAllProductData(eligibleMap);

  return { products: products, totalRemaining: totalRemaining };
}

/**
 * Query co2 for each cart item. Returns unique rules
 * where the condition is met and discount slots remain.
 */
async function getMatchingRules(cart) {
  var rules = [];
  var seen = new Set();

  for (var i = 0; i < cart.items.length; i++) {
    var item = cart.items[i];
    var result = await co2.conditionalDiscounts(item.product_id, item.variant_id, []);
    if (!result || !result.condition_applies) continue;

    for (var j = 0; j < result.condition_applies.length; j++) {
      var rule = result.condition_applies[j];

      // Deduplicate by rule signature
      var key = (rule.disc_perc || 0) + '_' + (rule.cond_base || '') + '_' + (rule.quant || 0);
      if (seen.has(key)) continue;
      seen.add(key);

      // Fresh trigger count (co2's internal state may be stale)
      var triggerIds = extractIds(rule.cond_product_items, rule.cond_product_variant_items);
      var trigger = countCartMatches(cart.items, triggerIds.productIds, triggerIds.variantIds);

      // Check if condition is met
      var met = false;
      if (rule.cond_minimum === 'quant') met = trigger.count >= rule.cond_quant;
      if (rule.cond_minimum === 'threshold') met = trigger.subtotal >= rule.cond_threshold * 100;
      if (!met) continue;

      // Fresh redeemed count (co2's rule.count may be stale)
      var eligibleIds = extractIds(rule.product_items, rule.product_variant_items);
      var redeemed = countCartMatches(cart.items, eligibleIds.productIds, eligibleIds.variantIds);

      var remaining = calcRemaining(rule, trigger, redeemed);
      if (remaining <= 0) continue;

      rules.push({ rule: rule, remaining: remaining });
    }
  }

  return rules;
}

/**
 * Resolve product IDs → handles for each matching rule.
 * Returns a deduplicated Map of handle → { discPerc, allowedVariantIds }.
 */
async function collectEligibleHandles(matchingRules) {
  var eligibleMap = new Map();

  for (var i = 0; i < matchingRules.length; i++) {
    var rule = matchingRules[i].rule;
    var pids = rule.product_items || [];
    var pvis = rule.product_variant_items || [];

    // Gather all IDs to resolve in one batch
    var idsToResolve = pids.slice();
    for (var j = 0; j < pvis.length; j++) {
      idsToResolve.push(pvis[j].pid || pvis[j]);
    }

    var handleMap = await resolveProductIds(idsToResolve);

    // Product-level items
    for (var k = 0; k < pids.length; k++) {
      var handle = handleMap.get(pids[k]);
      if (handle) addToEligibleMap(eligibleMap, handle, rule.disc_perc || 0, null);
    }

    // Variant-level items (with allowed variant filtering)
    for (var v = 0; v < pvis.length; v++) {
      var pid = pvis[v].pid || pvis[v];
      var h = handleMap.get(pid);
      if (!h) continue;
      var allowed = (pvis[v].vids && pvis[v].vids.length > 0) ? new Set(pvis[v].vids) : null;
      addToEligibleMap(eligibleMap, h, rule.disc_perc || 0, allowed);
    }
  }

  return eligibleMap;
}

/**
 * Fetch full Shopify product data for all eligible handles (parallel).
 */
async function fetchAllProductData(eligibleMap) {
  var handles = [];
  var infos = [];
  eligibleMap.forEach(function (info, handle) {
    handles.push(handle);
    infos.push(info);
  });

  var results = await Promise.all(handles.map(fetchProductByHandle));

  var products = [];
  for (var i = 0; i < results.length; i++) {
    if (!results[i] || !results[i].available) continue;
    products.push({
      handle: infos[i].handle,
      discPerc: infos[i].discPerc,
      allowedVariantIds: infos[i].allowedVariantIds,
      productData: results[i],
    });
  }
  return products;
}

// ─── 3. Rendering ──────────────────────────────────────────

function renderSlider(products, totalRemaining, settings) {
  var container = getSliderContainer();
  if (!container) return;

  if (!products.length || totalRemaining <= 0) {
    container.hidden = true;
    return;
  }

  var headerEl = container.querySelector('.datora-upsell__header');
  if (settings.headerText) {
    headerEl.innerHTML = settings.headerText.replace('[[COUNT]]', String(totalRemaining));
    headerEl.hidden = false;
  } else {
    headerEl.hidden = true;
  }

  // Destroy previous Flickity instance before rebuilding
  if (flickityInstance) {
    try { flickityInstance.destroy(); } catch (e) {}
    flickityInstance = null;
  }

  var inner = container.querySelector('.datora-upsell__products-inner');
  inner.innerHTML = '';
  for (var i = 0; i < products.length; i++) {
    inner.appendChild(renderProductCard(products[i], settings));
  }
  container.hidden = false;

  // Wait one frame so the browser can lay out the now-visible cards
  // before Flickity measures their dimensions
  requestAnimationFrame(function () {
    initFlickity(container.querySelector('.datora-upsell__products-inner')).then(function () {
      // If the drawer is already open (e.g. user changed quantity),
      // scroll to the slider right away
      var drawer = container.closest('cart-drawer');
      if (drawer && drawer.hasAttribute('open')) {
        requestAnimationFrame(scrollToSlider);
      }
    });
  });
}

function renderProductCard(item, settings) {
  var pd = item.productData;
  var url = window.Shopify.routes.root + 'products/' + pd.handle;

  var card = document.createElement('div');
  card.className = 'datora-upsell__card';

  // Image link
  var link = document.createElement('a');
  link.href = url;
  link.className = 'datora-upsell__card-link';
  var img = document.createElement('img');
  img.className = 'datora-upsell__card-image';
  img.loading = 'lazy';
  if (pd.featured_image) {
    img.src = getSizedImageUrl(pd.featured_image, '260x');
    img.alt = pd.title;
  }
  link.appendChild(img);
  card.appendChild(link);

  // Title
  var title = document.createElement('p');
  title.className = 'datora-upsell__card-title';
  title.textContent = pd.title;
  card.appendChild(title);

  // Price (original struck through + discounted)
  var priceWrap = document.createElement('div');
  priceWrap.className = 'datora-upsell__card-price';
  var cheapest = getCheapestAvailableVariant(pd, item.allowedVariantIds);
  if (cheapest) {
    var orig = document.createElement('span');
    orig.className = 'price price--compare';
    orig.innerHTML = safeFormatPrice(cheapest.price);
    priceWrap.appendChild(orig);

    var disc = document.createElement('span');
    disc.className = 'price price--highlight';
    disc.innerHTML = safeFormatPrice(Math.round(cheapest.price * (1 - item.discPerc / 100)));
    priceWrap.appendChild(disc);
  }
  card.appendChild(priceWrap);

  // CTA button — opens upsell modal
  var btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'datora-upsell__card-btn';
  btn.textContent = settings.btnText;
  btn.setAttribute('data-upsell-trigger', '');
  btn.setAttribute('data-upsell-href', window.Shopify.routes.root + 'products/' + pd.handle);
  card.appendChild(btn);

  return card;
}

// ─── 4. Flickity carousel ──────────────────────────────────

async function initFlickity(el) {
  if (!el) return;

  // Flickity is lazy-loaded in this theme — ensure the script is loaded first
  if (!window.ThemeFlickity && !window.Flickity) {
    if (typeof LibraryLoader !== 'undefined') {
      await LibraryLoader.load('flickity');
    } else {
      return;
    }
  }

  var FlickityClass = window.ThemeFlickity || window.Flickity;
  if (!FlickityClass) return;

  flickityInstance = new FlickityClass(el, {
    contain: true,
    cellSelector: '.datora-upsell__card',
    cellAlign: 'left',
    pageDots: false,
    prevNextButtons: false,
    draggable: '>1',
    freeScroll: true,
    percentPosition: false,
    rightToLeft: window.themeVariables && window.themeVariables.settings.direction === 'rtl'
  });

  // Equalize card heights so buttons align at the bottom
  var maxH = 0;
  var cards = el.querySelectorAll('.datora-upsell__card');
  for (var i = 0; i < cards.length; i++) {
    if (cards[i].offsetHeight > maxH) maxH = cards[i].offsetHeight;
  }
  if (maxH > 0) {
    for (var j = 0; j < cards.length; j++) cards[j].style.height = maxH + 'px';
    flickityInstance.resize();
  }

  if (flickityInstance.slides && flickityInstance.slides.length > 1) {
    flickityInstance.slides[flickityInstance.slides.length - 1].target += 24;
  }
}

// ─── 5. Helpers ────────────────────────────────────────────

function getSliderContainer() {
  return document.getElementById('datora-upsell-slider');
}

/** Scrolls the cart drawer so the upsell slider is visible. */
function scrollToSlider() {
  var container = getSliderContainer();
  if (!container || container.hidden) return;
  var scrollable = container.closest('.drawer__content');
  if (!scrollable) return;

  // Calculate offset relative to the scrollable container
  var offset = 0;
  var el = container;
  while (el && el !== scrollable) {
    offset += el.offsetTop;
    el = el.offsetParent;
  }
  scrollable.scrollTo({ top: offset, behavior: 'smooth' });
}

function getSettings(container) {
  return {
    headerText: container.dataset.headerText || '',
    btnText: container.dataset.btnText || 'Auswählen',
  };
}

function safeFormatPrice(cents) {
  if (typeof formatPrice === 'function') return formatPrice(cents);
  return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
}

function getCheapestAvailableVariant(productData, allowedVariantIds) {
  var cheapest = null;
  for (var i = 0; i < productData.variants.length; i++) {
    var v = productData.variants[i];
    if (!v.available) continue;
    if (allowedVariantIds && !allowedVariantIds.has(v.id)) continue;
    if (!cheapest || v.price < cheapest.price) cheapest = v;
  }
  return cheapest || productData.variants[0];
}

function getSizedImageUrl(src, size) {
  if (!src) return '';
  var match = src.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
  if (match) return src.replace(match[0], '_' + size + '.' + match[1] + (match[2] || ''));
  return src;
}

// ─── 6. Rule helpers ──────────────────────────────────────

/** Builds Sets of product IDs and variant IDs from co2 rule arrays. */
function extractIds(productItems, variantItems) {
  var productIds = new Set(productItems || []);
  var variantIds = new Set();
  var items = variantItems || [];
  for (var i = 0; i < items.length; i++) {
    if (items[i].vids) {
      for (var j = 0; j < items[i].vids.length; j++) variantIds.add(items[i].vids[j]);
    }
    if (items[i].pid) productIds.add(items[i].pid);
  }
  return { productIds: productIds, variantIds: variantIds };
}

/** Counts how many cart items match a set of product/variant IDs. */
function countCartMatches(cartItems, productIds, variantIds) {
  var count = 0;
  var subtotal = 0;
  for (var i = 0; i < cartItems.length; i++) {
    var ci = cartItems[i];
    if (productIds.has(ci.product_id) || variantIds.has(ci.variant_id)) {
      count += ci.quantity;
      subtotal += ci.final_line_price;
    }
  }
  return { count: count, subtotal: subtotal };
}

/** Calculates remaining discount slots for a rule. */
function calcRemaining(rule, trigger, redeemed) {
  var max = 0;
  if (rule.multi_apply === 'once') {
    max = rule.quant;
  } else if (rule.multi_apply === 'multiply') {
    if (rule.cond_minimum === 'quant') {
      max = Math.floor(trigger.count / rule.cond_quant) * rule.quant;
    } else {
      max = Math.floor(trigger.subtotal / (rule.cond_threshold * 100)) * rule.quant;
    }
  } else {
    max = Infinity;
  }
  return Math.max(0, max - redeemed.count);
}

/** Sums remaining slots across all matching rules. */
function sumRemaining(matchingRules) {
  var total = 0;
  for (var i = 0; i < matchingRules.length; i++) total += matchingRules[i].remaining;
  return total;
}

/** Adds a product handle to the eligible map, merging allowed variants. */
function addToEligibleMap(map, handle, discPerc, allowedVariantIds) {
  if (!map.has(handle)) {
    map.set(handle, { handle: handle, discPerc: discPerc, allowedVariantIds: allowedVariantIds });
  } else if (allowedVariantIds) {
    var existing = map.get(handle);
    if (existing.allowedVariantIds) {
      allowedVariantIds.forEach(function (vid) { existing.allowedVariantIds.add(vid); });
    } else {
      existing.allowedVariantIds = allowedVariantIds;
    }
  }
}

// ─── 7. Network ───────────────────────────────────────────

async function fetchCart() {
  var resp = await fetch(window.Shopify.routes.root + 'cart.js');
  return resp.json();
}

async function fetchProductByHandle(handle) {
  if (productDataCache.has(handle)) return productDataCache.get(handle);
  try {
    var resp = await fetch(window.Shopify.routes.root + 'products/' + handle + '.js');
    if (!resp.ok) return null;
    var data = await resp.json();
    productDataCache.set(handle, data);
    return data;
  } catch (e) {
    return null;
  }
}

/** Resolves numeric Shopify product IDs to handles via Storefront API. */
async function resolveProductIds(pids) {
  var resolved = new Map();
  if (!pids.length) return resolved;

  var client = getStorefrontClient();
  if (!client) return resolved;

  try {
    var resp = await fetch(
      (client.domain.indexOf('://') !== -1 ? client.domain : 'https://' + client.domain) +
      '/api/' + client.version + '/graphql.json',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': client.token,
        },
        body: JSON.stringify({
          query: 'query($ids:[ID!]!){nodes(ids:$ids){...on Product{id handle}}}',
          variables: { ids: pids.map(function (id) { return 'gid://shopify/Product/' + id; }) },
        }),
      }
    );
    if (resp.ok) {
      var json = await resp.json();
      if (json.data && json.data.nodes) {
        for (var i = 0; i < json.data.nodes.length; i++) {
          var node = json.data.nodes[i];
          if (node && node.handle) {
            resolved.set(parseInt(node.id.split('/').pop(), 10), node.handle);
          }
        }
      }
    }
  } catch (e) { /* graceful degradation */ }

  return resolved;
}

function getStorefrontClient() {
  if (storefrontClient) return storefrontClient;
  try {
    var el = document.querySelector('#storefront_api_client_data');
    if (!el) return null;
    var data = JSON.parse(el.text);
    var token = data.public_access_token ? atob(data.public_access_token) : null;
    if (!token || !data.store_domain) return null;
    storefrontClient = { domain: data.store_domain, token: token, version: data.api_version || '2024-10' };
    return storefrontClient;
  } catch (e) {
    return null;
  }
}

// ─── Shared globals (used by datora-conditional-modal.js) ─
window.datoraGetStorefrontClient = getStorefrontClient;
window.datoraSafeFormatPrice = safeFormatPrice;

// ─── 8. Auto-init on cart drawer re-render ────────────────

document.documentElement.addEventListener('cart:rerendered', function () {
  initUpsellSlider();
});

// Scroll to the upsell slider when the cart drawer opens.
// The drawer's open animation resets scrollTop=0 and runs ~800ms,
// so we wait for it to finish before scrolling.
(function () {
  var drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].attributeName === 'open' && drawer.hasAttribute('open')) {
        setTimeout(scrollToSlider, 1000);
        return;
      }
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
})();

